import React from "react";
import { PeriodCalendar } from "./PeriodCalendar";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  MOTHER_BED_PROFILE_ID_SET,
  MOTHER_BED_PROFILE_OPTIONS,
} from "./hospital/motherProfiles";
import { getMonthLabel } from "./hospital/types";
import { buildQualitySlides } from "./quality/QualityWidgets";
import { HospitalDeptFinanceSlide, HospitalDeptServicesSlide } from "./HospitalReportSlides";
import { fetchHospitalReport } from "./hospital/api";
import { useHospitalDashboard } from "./hospital/hooks/useHospitalDashboard";

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorText: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorText: "" };
  }

  static getDerivedStateFromError(err: unknown) {
    return {
      hasError: true,
      errorText: String((err as any)?.stack || (err as any)?.message || err),
    };
  }

  componentDidCatch(err: unknown) {
    console.error("HospitalDashboardsPage crashed:", err);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 16,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            color: "#b00020",
          }}
        >
          <h2 style={{ margin: "0 0 10px 0" }}>
            Ошибка в HospitalDashboardsPage
          </h2>
          {this.state.errorText}
        </div>
      );
    }
    return this.props.children as any;
  }
}

type OrgOption = {
  id: string;
  label: string;
};

type HospitalChipKey =
  | "mis_id"
  | "place"
  | "finance_source_id"
  | "year"
  | "month"
  | "year_b"
  | "month_b"
  | "year_p"
  | "bed_profile_id"
  | "month_p"
  | "year_m"
  | "mkb10_id"
  | "month_m"
  | "year_d"
  | "department_id"
  | "performer_employee_id"
  | "month_d";

type SlideItem = {
  id: string;
  element: React.ReactNode;
};

const PAGE_LAYOUTS = [1, 2, 3, 4, 6] as const;
type PageLayout = (typeof PAGE_LAYOUTS)[number];

const LAYOUT_STORAGE_KEY = "dashboards_hospital_layout_by_page";
const DEFAULT_LAYOUT: PageLayout = 2;
const HOSPITAL_ECHARTS_TOOLTIP = {
  backgroundColor: "#083264",
  borderColor: "rgba(255,255,255,0.3)",
  borderWidth: 1,
  textStyle: { color: "#fff" },
  extraCssText: "border-radius:10px;",
};

type SlidePresentationContextValue = {
  activeSlideId: string | null;
  setActiveSlideId: (slideId: string | null) => void;
};

const SlidePresentationContext =
  React.createContext<SlidePresentationContextValue>({
    activeSlideId: null,
    setActiveSlideId: () => undefined,
  });

const LayoutContext = React.createContext<PageLayout>(DEFAULT_LAYOUT);

function isValidLayout(value: unknown): value is PageLayout {
  return (
    value === 1 || value === 2 || value === 3 || value === 4 || value === 6
  );
}

function readLayoutByPageFromStorage(): Record<number, PageLayout> {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const next: Record<number, PageLayout> = {};
    Object.entries(parsed || {}).forEach(([k, v]) => {
      const idx = Number(k);
      if (!Number.isInteger(idx) || idx < 0 || !isValidLayout(v)) return;
      next[idx] = v;
    });
    return next;
  } catch {
    return {};
  }
}

function writeLayoutByPageToStorage(layoutByPage: Record<number, PageLayout>) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layoutByPage));
  } catch {
    // ignore
  }
}

function chunkSlides<T>(slides: T[], layoutByPage: Record<number, PageLayout>) {
  const pages: T[][] = [];
  let cursor = 0;
  let page = 0;

  while (cursor < slides.length) {
    const capacity = layoutByPage[page] ?? DEFAULT_LAYOUT;
    pages.push(slides.slice(cursor, cursor + capacity));
    cursor += capacity;
    page += 1;
  }

  return pages;
}

function MisIdDropdown({
  value,
  options,
  onChange,
}: {
  value: string;
  options: OrgOption[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((x) => x.id === value);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 240, maxWidth: 320 }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          height: 36,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: "#083264",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedOption?.label || "— выбрать —"}
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: "#083264",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 10,
            zIndex: 100,
          }}
        >
          {[{ id: "", label: "— выбрать —" }, ...options].map((org) => (
            <div
              key={`mis-${org.id || "empty"}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                onChange(org.id);
              }}
              style={{
                padding: "8px 10px",
                color: "#fff",
                cursor: "pointer",
                fontWeight: value === org.id ? 700 : 500,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.fontWeight = "700";
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.fontWeight =
                  value === org.id ? "700" : "500";
                e.currentTarget.style.background = "transparent";
              }}
            >
              {org.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FiltersDropdown({
  chips,
  onRemove,
}: {
  chips: Array<{ key: HospitalChipKey; label: string }>;
  onRemove: (key: HospitalChipKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        minWidth: 240,
        width: 300,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          height: 36,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: "#083264",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Фильтры
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: 320,
            overflowY: "auto",
            background: "#083264",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 10,
            zIndex: 120,
          }}
        >
          {!chips.length ? (
            <div style={{ padding: "10px 10px", color: "#fff", opacity: 0.9 }}>
              Активных фильтров нет.
            </div>
          ) : (
            chips.map((c) => (
              <div
                key={`chip-${String(c.key)}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  color: "#fff",
                }}
              >
                <button
                  type="button"
                  title="Убрать фильтр"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(c.key);
                  }}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(0,0,0,0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    lineHeight: "20px",
                    fontWeight: 800,
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
                <div
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function getGridTemplate(layout: PageLayout) {
  if (layout === 1)
    return { gridTemplateColumns: "1fr", gridTemplateRows: "1fr" };
  if (layout === 2)
    return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr" };
  if (layout === 3)
    return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  if (layout === 4)
    return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  return { gridTemplateColumns: "repeat(3, 1fr)", gridTemplateRows: "1fr 1fr" };
}

function renderPageCells(slides: SlideItem[], layout: PageLayout) {
  const cellStyle: React.CSSProperties = {
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  if (layout === 3 && slides.length >= 3) {
    return [
      <div key={slides[0].id} style={cellStyle}>
        {slides[0].element}
      </div>,
      <div key={slides[1].id} style={cellStyle}>
        {slides[1].element}
      </div>,
      <div
        key={slides[2].id}
        style={{ ...cellStyle, gridColumn: "1 / span 2" }}
      >
        {slides[2].element}
      </div>,
    ];
  }

  const cells = slides.map((slide) => (
    <div key={slide.id} style={cellStyle}>
      {slide.element}
    </div>
  ));

  if (layout === 6 && slides.length === 5) {
    cells.push(
      <div
        key="placeholder-6"
        style={{
          ...cellStyle,
          border: "1px dashed rgba(255,255,255,0.18)",
          borderRadius: 12,
        }}
      />,
    );
  }

  return cells;
}

function ErrorBox({ error }: { error: string }) {
  return (
    <div
      style={{
        marginTop: 10,
        borderRadius: 10,
        border: "1px solid rgba(255,100,100,0.45)",
        background: "rgba(120,0,0,0.18)",
        color: "#ffd4d4",
        padding: "10px 12px",
        whiteSpace: "pre-wrap",
      }}
    >
      {error}
    </div>
  );
}

function formatDayNumber(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return String(date.getDate());
}

function getLatestFundingYear(rows: { year: number }[]): number | null {
  if (!rows.length) return null;
  return rows.reduce((max, row) => Math.max(max, row.year), rows[0].year);
}

function truncateWords(value: string, maxWords = 3): string {
  const words = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length <= maxWords) return words.join(" ");
  return `${words.slice(0, maxWords).join(" ")}…`;
}

function horizontalAxisLabels(
  labels: string[],
  {
    formatter,
  }: {
    formatter?: (value: string) => string;
  } = {},
) {
  return {
    ...(baseAxisOption() as any).yAxis,
    type: "category",
    inverse: true,
    data: labels,
    axisLabel: {
      ...(baseAxisOption() as any).yAxis.axisLabel,
      formatter: formatter ?? ((value: string) => truncateWords(String(value))),
    },
  };
}

function sortByTotalDesc<T extends { total_count: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => b.total_count - a.total_count);
}

function SlideContainer(props: {
  title: string;
  hintRight?: string;
  tableHref?: string;
  children: ReactNode;
}) {
  const slideId = React.useId();
  const { activeSlideId, setActiveSlideId } = React.useContext(
    SlidePresentationContext,
  );
  const pageLayout = React.useContext(LayoutContext);
  const isFullscreen = activeSlideId === slideId;

  const overlayZ = 15000;
  const cardZ = 16000;

  const titleFontSize = React.useMemo(() => {
    if (isFullscreen) return 20;
    if (pageLayout === 6) return 11;
    if (pageLayout === 4) return 13;
    if (pageLayout === 3) return 15;
    if (pageLayout === 2) return 17;
    return 18;
  }, [isFullscreen, pageLayout]);

  const titleLineHeight =
    titleFontSize <= 11 ? 1.15 : titleFontSize <= 13 ? 1.2 : 1.25;

  return (
    <div style={{ height: "100%", minHeight: 0 }}>
      {isFullscreen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,5,20,0.55)",
            zIndex: overlayZ,
          }}
          onClick={() => setActiveSlideId(null)}
        />
      ) : null}

      <section
        style={{
          boxSizing: "border-box",
          background: "#0e2b4b",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.24)",
          borderRadius: 12,
          padding: 12,
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
          minHeight: 0,
          height: isFullscreen ? "calc(100vh - 32px)" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: isFullscreen ? "fixed" : "relative",
          left: isFullscreen ? "50%" : undefined,
          top: isFullscreen ? "50%" : undefined,
          transform: isFullscreen ? "translate(-50%, -50%)" : undefined,
          width: isFullscreen ? "min(1400px, calc(100vw - 32px))" : "100%",
          zIndex: isFullscreen ? cardZ : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            minWidth: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: titleFontSize,
              lineHeight: titleLineHeight,
              fontWeight: 700,
              minWidth: 0,
              flex: 1,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical" as any,
              overflow: "hidden",
              WebkitLineClamp: isFullscreen ? 3 : pageLayout >= 4 ? 3 : 2,
            }}
            title={props.title}
          >
            {props.title}
          </h2>

          {props.tableHref ? (
            <button
              onClick={(e) => { e.stopPropagation(); window.open(props.tableHref, "_blank", "noopener,noreferrer"); }}
              title="Открыть таблицу случаев этого среза"
              style={{ flexShrink: 0, height: 26, padding: "0 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.35)", background: "transparent", color: "#fff", cursor: "pointer", fontSize: 13 }}
            >
              📋
            </button>
          ) : null}

          {props.hintRight ? (
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.75)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {props.hintRight}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setActiveSlideId(isFullscreen ? null : slideId)}
            style={{
              height: 32,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.28)",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              flexShrink: 0,
            }}
            title={isFullscreen ? "Свернуть" : "Развернуть"}
          >
            {isFullscreen ? "Свернуть" : "Развернуть"}
          </button>
        </div>

        <div
          style={{
            marginTop: 10,
            minHeight: 0,
            flex: 1,
            overflow: "hidden",
          }}
        >
          {props.children}
        </div>
      </section>
    </div>
  );
}

type ChartClickParam = {
  name?: string | number;
  value?: number | string;
  data?: Record<string, unknown>;
  seriesName?: string;
};

function HospitalChart(props: {
  option: Record<string, unknown>;
  onClick?: (params: ChartClickParam) => void;
}) {
  return (
    <ReactECharts
      option={props.option as any}
      style={{ width: "100%", height: "100%", minHeight: 0, margin: "0 auto" }}
      lazyUpdate={true}
      onEvents={props.onClick ? { click: props.onClick } : undefined}
    />
  );
}

function withHorizontalBarLogScale(option: Record<string, unknown>) {
  const xAxis = option.xAxis as Record<string, unknown> | undefined;
  const yAxis = option.yAxis as Record<string, unknown> | undefined;
  const series = Array.isArray(option.series) ? option.series : [];
  if (!xAxis || !yAxis || !series.length) return option;
  if (xAxis.type !== "value" || yAxis.type !== "category") return option;

  const firstSeries = series[0] as Record<string, unknown> | undefined;
  if (!firstSeries || firstSeries.type !== "bar" || !Array.isArray(firstSeries.data)) {
    return option;
  }

  const rawVals = firstSeries.data.map((item) => {
    if (typeof item === "number") return item;
    if (typeof item === "string") return Number(item);
    return Number((item as Record<string, unknown> | null)?.value ?? 0);
  });
  const positives = rawVals.filter((v) => Number.isFinite(v) && v > 0);
  const minPositive = positives.length ? Math.min(...positives) : 1;
  const maxPositive = positives.length ? Math.max(...positives) : 1;
  const axisMin = Math.max(1e-6, minPositive * 0.9);
  const eps = axisMin * 1.001;

  const nextSeries = series.map((seriesItem, seriesIndex) => {
    const typed = seriesItem as Record<string, unknown>;
    if (typed.type !== "bar" || !Array.isArray(typed.data)) return seriesItem;
    return {
      ...typed,
      data: typed.data.map((point, pointIndex) => {
        const raw = seriesIndex === 0 ? rawVals[pointIndex] : (
          typeof point === "number" ? point : typeof point === "string" ? Number(point) : Number((point as Record<string, unknown> | null)?.value ?? 0)
        );
        const scaled = raw > 0 ? raw : eps;
        if (typeof point === "number" || typeof point === "string") return scaled;
        return { ...(point as Record<string, unknown>), value: scaled, rawValue: raw };
      }),
    };
  });

  return {
    ...option,
    tooltip: {
      ...((option.tooltip as Record<string, unknown> | undefined) ?? {}),
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (items: any) => {
        const it = Array.isArray(items) ? items[0] : items;
        const idx = Number(it?.dataIndex);
        const real = Number.isFinite(idx) ? rawVals[idx] : undefined;
        const name = it?.axisValueLabel ?? it?.name ?? "";
        return `${name}<br/>${real ?? ""}`;
      },
    },
    xAxis: {
      ...xAxis,
      type: "log",
      logBase: 10,
      min: axisMin,
      max: maxPositive * 1.05,
      minorSplitLine: { show: true },
      axisLabel: {
        ...((xAxis.axisLabel as Record<string, unknown> | undefined) ?? {}),
        formatter: (v: unknown) => {
          const n = Number(v);
          if (!Number.isFinite(n)) return "";
          if (n === 1) return "1";
          if (n === 10) return "10";
          if (n === 100) return "100";
          if (n === 1000) return "1000";
          if (n === 10000) return "10000";
          return "";
        },
      },
    },
    series: nextSeries,
  };
}

function withHorizontalBarDataZoom(option: Record<string, unknown>) {
  return withHorizontalBarLogScale({
    ...option,
    dataZoom: [
      { type: "inside", yAxisIndex: 0, filterMode: "none" },
      {
        type: "slider",
        yAxisIndex: 0,
        filterMode: "none",
        right: 8,
        top: 10,
        bottom: 10,
        width: 14,
        showDetail: false,
      },
    ],
  });
}

function baseAxisOption() {
  return {
    tooltip: { ...HOSPITAL_ECHARTS_TOOLTIP, trigger: "axis" },
    grid: { top: 28, left: 44, right: 24, bottom: 58, containLabel: true },
    xAxis: {
      axisLabel: { color: "rgba(255,255,255,0.82)", rotate: 25 },
      axisLine: { lineStyle: { color: "rgba(255,255,255,0.3)" } },
    },
    yAxis: {
      axisLabel: { color: "rgba(255,255,255,0.82)" },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.16)" } },
    },
    legend: { textStyle: { color: "rgba(255,255,255,0.9)" } },
  };
}

function HospitalSlideBox(props: {
  title: string;
  loading?: boolean;
  error?: string | null;
  tableHref?: string;
  children: ReactNode;
}) {
  return (
    <SlideContainer title={props.title} tableHref={props.tableHref}>
      {props.loading ? (
        <div style={{ color: "#fff", opacity: 0.9 }}>Загрузка…</div>
      ) : props.error ? (
        <ErrorBox error={props.error} />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            display: "flex",
            alignItems: "stretch",
            justifyContent: "stretch",
            overflow: "hidden",
          }}
        >
          {props.children}
        </div>
      )}
    </SlideContainer>
  );
}

function buildChips(params: {
  filters: ReturnType<typeof useHospitalDashboard>["filters"];
  charts: ReturnType<typeof useHospitalDashboard>["charts"];
}): Array<{ key: HospitalChipKey; label: string }> {
  const { filters, charts } = params;
  const out: Array<{ key: HospitalChipKey; label: string }> = [];

  const orgLabel =
    charts.bootstrap?.available_orgs.find((x) => x.mis_id === filters.mis_id)
      ?.org_name || filters.mis_id;

  if (filters.mis_id)
    out.push({ key: "mis_id", label: `Подразделение: ${orgLabel}` });
  if (filters.place)
    out.push({ key: "place", label: `Place: ${filters.place}` });

  if (filters.finance_source_id !== null && filters.finance_source_id !== undefined) {
    const row = charts.slide2_yearFunding.find(
      (x) => x.finance_source_id === filters.finance_source_id,
    );
    out.push({
      key: "finance_source_id",
      label: `Источник финансирования: ${row?.finance_source_name || filters.finance_source_id}`,
    });
  }

  if (filters.year !== null && filters.year !== undefined)
    out.push({ key: "year", label: `Год: ${filters.year}` });
  if (filters.month !== null && filters.month !== undefined)
    out.push({ key: "month", label: `Месяц: ${getMonthLabel(filters.month)}` });

  if (filters.year_b !== null && filters.year_b !== undefined)
    out.push({ key: "year_b", label: `Год койко-дней: ${filters.year_b}` });
  if (filters.month_b !== null && filters.month_b !== undefined)
    out.push({
      key: "month_b",
      label: `Месяц койко-дней: ${getMonthLabel(filters.month_b)}`,
    });

  if (filters.year_p !== null && filters.year_p !== undefined)
    out.push({ key: "year_p", label: `Год профиля койки: ${filters.year_p}` });
  if (filters.bed_profile_id !== null && filters.bed_profile_id !== undefined) {
    const row = charts.slide7_bedProfileYear.find(
      (x) => x.bed_profile_id === filters.bed_profile_id,
    );
    out.push({
      key: "bed_profile_id",
      label: `Профиль койки: ${row?.bed_profile_name || filters.bed_profile_id}`,
    });
  }
  if (filters.month_p !== null && filters.month_p !== undefined)
    out.push({
      key: "month_p",
      label: `Месяц профиля койки: ${getMonthLabel(filters.month_p)}`,
    });

  if (filters.year_m !== null && filters.year_m !== undefined)
    out.push({ key: "year_m", label: `Год МКБ-10: ${filters.year_m}` });
  if (filters.mkb10_id !== null && filters.mkb10_id !== undefined) {
    const row = charts.slide8_mkb10Year.find(
      (x) => x.mkb10_id === filters.mkb10_id,
    );
    out.push({
      key: "mkb10_id",
      label: `МКБ-10: ${row?.mkb10_code || filters.mkb10_id}`,
    });
  }
  if (filters.month_m !== null && filters.month_m !== undefined)
    out.push({
      key: "month_m",
      label: `Месяц МКБ-10: ${getMonthLabel(filters.month_m)}`,
    });

  if (filters.year_d !== null && filters.year_d !== undefined)
    out.push({ key: "year_d", label: `Год отделений: ${filters.year_d}` });
  if (filters.department_id !== null && filters.department_id !== undefined) {
    const row = charts.slide9_departments.find(
      (x) => x.department_id === filters.department_id,
    );
    out.push({
      key: "department_id",
      label: `Отделение: ${row?.department_name || filters.department_id}`,
    });
  }
  if (
    filters.performer_employee_id !== null &&
    filters.performer_employee_id !== undefined
  ) {
    const row = charts.slide10_doctors.find(
      (x) => x.performer_employee_id === filters.performer_employee_id,
    );
    out.push({
      key: "performer_employee_id",
      label: `Врач: ${row?.doctor_name || filters.performer_employee_id}`,
    });
  }
  if (filters.month_d !== null && filters.month_d !== undefined)
    out.push({
      key: "month_d",
      label: `Месяц врача: ${getMonthLabel(filters.month_d)}`,
    });

  return out;
}

export default function HospitalDashboardsPage() {
  const pathname = window.location.pathname || "/";
  const isMotherMode = pathname.startsWith("/dashboards/mother-hospital");
  const {
    filters,
    charts,
    loading,
    errors,
    patchFilters,
    resetFiltersAndReload,
    onSelectMisId,
    onSelectPlace,
    onSelectYearFunding,
    onSelectTreatmentOutcome,
    onSelectCasesMonth,
    onSelectBedDaysYear,
    onSelectBedDaysMonth,
    onSelectBedProfile,
    onSelectBedProfileMonth,
    onSelectMkb10,
    onSelectMkb10Month,
    onSelectDepartment,
    onSelectDoctor,
    onSelectDoctorMonth,
    reloadAllVisibleData,
  } = useHospitalDashboard({ mode: isMotherMode ? "mother" : "standard" });

  const orgOptions = useMemo<OrgOption[]>(
    () =>
      (charts.bootstrap?.available_orgs ?? []).map((org) => ({
        id: org.mis_id,
        label: org.org_name,
      })),
    [charts.bootstrap?.available_orgs],
  );

  const chips = useMemo(
    () => buildChips({ filters, charts }),
    [filters, charts],
  );

  const clearFilter = React.useCallback(
    (key: HospitalChipKey) => {
      if (key === "mis_id") {
        void resetFiltersAndReload();
        return;
      }

      if (isMotherMode && (key === "place" || key === "finance_source_id")) {
        return;
      }

      const patch: Partial<typeof filters> = {};

      if (key === "place") {
        patch.place = null;
        patch.finance_source_id = null;
        patch.treatment_outcome_id = null;

        patch.year = null;
        patch.month = null;
        patch.day = null;

        patch.year_b = null;
        patch.month_b = null;

        patch.year_p = null;
        patch.bed_profile_id = null;
        patch.month_p = null;

        patch.year_m = null;
        patch.mkb10_id = null;
        patch.month_m = null;

        patch.year_d = null;
        patch.department_id = null;
        patch.performer_employee_id = null;
        patch.month_d = null;
      } else if (key === "finance_source_id") {
        patch.finance_source_id = null;
        patch.treatment_outcome_id = null;

        patch.month = null;
        patch.day = null;

        patch.month_b = null;

        patch.bed_profile_id = null;
        patch.month_p = null;

        patch.mkb10_id = null;
        patch.month_m = null;

        patch.department_id = null;
        patch.performer_employee_id = null;
        patch.month_d = null;
      } else if (key === "year") {
        patch.year = null;
        patch.month = null;
        patch.day = null;
      } else if (key === "month") {
        patch.month = null;
        patch.day = null;
      } else if (key === "year_b") {
        patch.year_b = null;
        patch.month_b = null;
      } else if (key === "month_b") {
        patch.month_b = null;
      } else if (key === "year_p") {
        patch.year_p = null;
        patch.bed_profile_id = null;
        patch.month_p = null;
      } else if (key === "bed_profile_id") {
        patch.bed_profile_id = null;
        patch.month_p = null;
      } else if (key === "month_p") {
        patch.month_p = null;
      } else if (key === "year_m") {
        patch.year_m = null;
        patch.mkb10_id = null;
        patch.month_m = null;
      } else if (key === "mkb10_id") {
        patch.mkb10_id = null;
        patch.month_m = null;
      } else if (key === "month_m") {
        patch.month_m = null;
      } else if (key === "year_d") {
        patch.year_d = null;
        patch.department_id = null;
        patch.performer_employee_id = null;
        patch.month_d = null;
      } else if (key === "department_id") {
        patch.department_id = null;
        patch.performer_employee_id = null;
        patch.month_d = null;
      } else if (key === "performer_employee_id") {
        patch.performer_employee_id = null;
        patch.month_d = null;
      } else if (key === "month_d") {
        patch.month_d = null;
      }

      patchFilters(patch);
      void reloadAllVisibleData(patch);
    },
    [isMotherMode, patchFilters, reloadAllVisibleData, resetFiltersAndReload],
  );

  const [layoutByPage, setLayoutByPage] = useState<Record<number, PageLayout>>(
    () => readLayoutByPageFromStorage(),
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);
  // ?page=last — открыть последнюю страницу (возврат << из закладки «Диспансерный учёт»)
  const wantLastPageRef = useRef(
    new URLSearchParams(window.location.search).get("page") === "last",
  );

  const hasSlide41 = filters.year_b !== null && filters.year_b !== undefined;
  const hasSlide42 =
    hasSlide41 && filters.month_b !== null && filters.month_b !== undefined;
  const hasSlide6 = filters.month !== null && filters.month !== undefined;

  const hasSlide71 =
    filters.year_p !== null &&
    filters.year_p !== undefined &&
    filters.bed_profile_id !== null &&
    filters.bed_profile_id !== undefined;
  const hasSlide72 =
    hasSlide71 && filters.month_p !== null && filters.month_p !== undefined;

  const hasSlide81 =
    filters.year_m !== null &&
    filters.year_m !== undefined &&
    filters.mkb10_id !== null &&
    filters.mkb10_id !== undefined;
  const hasSlide82 =
    hasSlide81 && filters.month_m !== null && filters.month_m !== undefined;

  const hasSlide10 =
    filters.year_d !== null &&
    filters.year_d !== undefined &&
    filters.department_id !== null &&
    filters.department_id !== undefined;
  const hasSlide11 =
    hasSlide10 &&
    filters.performer_employee_id !== null &&
    filters.performer_employee_id !== undefined;
  const hasSlide12 =
    hasSlide11 && filters.month_d !== null && filters.month_d !== undefined;

  const activeDrilldownYear =
    filters.year ?? getLatestFundingYear(charts.slide2_yearFunding);

  const casesTableHref = useMemo(() => {
    const qs = new URLSearchParams();
    if (filters.mis_id) qs.set("mis_id", String(filters.mis_id));
    if (filters.place) qs.set("place", String(filters.place));
    if (filters.year != null) qs.set("year", String(filters.year));
    if (filters.date_from) qs.set("date_from", String(filters.date_from));
    if (filters.date_to) qs.set("date_to", String(filters.date_to));
    if (filters.finance_source_id != null) qs.set("finance_source_id", String(filters.finance_source_id));
    if (filters.treatment_outcome_id != null) qs.set("treatment_outcome_id", String(filters.treatment_outcome_id));
    if (filters.bed_profile_id != null) qs.set("bed_profile_id", String(filters.bed_profile_id));
    if (filters.mkb10_id != null) qs.set("mkb10_id", String(filters.mkb10_id));
    if (filters.department_id != null) qs.set("department_id", String(filters.department_id));
    if (isMotherMode && filters.bed_profile_id == null) {
      qs.set("bed_profile_ids", MOTHER_BED_PROFILE_OPTIONS.map((o) => o.id).join(","));
    }
    qs.set("title", `Пролеченные случаи ${filters.place ?? "КС/ДС"}${isMotherMode ? " · охрана материнства" : ""}${filters.year != null ? ` · ${filters.year}` : ""}`);
    return `/dashboards/app/hospital-cases?${qs.toString()}`;
  }, [filters, isMotherMode]);

  const visibleSlides = useMemo<SlideItem[]>(() => {
    const fundingNameById = new Map(
      charts.slide2_yearFunding.map((item) => [
        String(item.finance_source_id ?? "null"),
        item.finance_source_name,
      ]),
    );
    const bedProfileRows = sortByTotalDesc(
      isMotherMode
        ? charts.slide7_bedProfileYear.filter(
            (item) =>
              item.bed_profile_id !== null &&
              MOTHER_BED_PROFILE_ID_SET.has(item.bed_profile_id),
          )
        : charts.slide7_bedProfileYear,
    );
    const mkb10Rows = sortByTotalDesc(charts.slide8_mkb10Year);
    const departmentRows = sortByTotalDesc(charts.slide9_departments);
    const doctorRows = sortByTotalDesc(charts.slide10_doctors);
    const selectedBedProfileName = charts.slide7_bedProfileYear.find((i) => i.bed_profile_id === filters.bed_profile_id)?.bed_profile_name;
    const selectedMkb10Code = charts.slide8_mkb10Year.find((i) => i.mkb10_id === filters.mkb10_id)?.mkb10_code;
    const selectedDoctorName = charts.slide10_doctors.find((i) => i.performer_employee_id === filters.performer_employee_id)?.doctor_name;
    const selectedDepartmentName = charts.slide9_departments.find((i) => i.department_id === filters.department_id)?.department_name;

    const slides: SlideItem[] = [
      {
        id: "hospital-slide-1",
        element: (
          <HospitalSlideBox
            title="Количество пролеченных случаев (ПС)"
            loading={loading.slide1_place}
            error={errors.slide1_place}
          >
            <HospitalChart
              option={{
                tooltip: { ...HOSPITAL_ECHARTS_TOOLTIP, trigger: "item" },
                legend: { bottom: 0, textStyle: { color: "#fff" } },
                series: [
                  {
                    type: "pie",
                    radius: ["35%", "72%"],
                    data: charts.slide1_place.map((item) => ({
                      name: `Количество случаев ${item.place}`,
                      value: item.total_count,
                      place: item.place,
                      selected: filters.place === item.place,
                    })),
                    label: { color: "#fff" },
                  },
                ],
              }}
              onClick={(p) => {
                const place = String((p.data?.place as string) || "");
                if (place === "КС" || place === "ДС") {
                  void onSelectPlace(place);
                }
              }}
            />
          </HospitalSlideBox>
        ),
      },
      {
        id: "hospital-slide-2",
        element: (
          <HospitalSlideBox
            title={`Количество ПС ${filters.place ?? "КС/ДС"} по ГОБМП и ОСМС по годам`}
            loading={loading.slide2_yearFunding}
            error={errors.slide2_yearFunding}
          >
            <HospitalChart
              option={(() => {
                const years = [...new Set(charts.slide2_yearFunding.map((i) => i.year))].sort((a, b) => a - b);
                const groups = [...new Set(charts.slide2_yearFunding.map((i) => String(i.finance_source_id ?? "null")))];
                return {
                  ...baseAxisOption(),
                  xAxis: { ...(baseAxisOption() as any).xAxis, type: "category", data: years },
                  yAxis: { ...(baseAxisOption() as any).yAxis, type: "value" },
                  series: groups.map((group) => ({
                    name: fundingNameById.get(group) ?? group,
                    type: "bar",
                    data: years.map((year) => {
                      const row = charts.slide2_yearFunding.find((i) => i.year === year && String(i.finance_source_id ?? "null") === group);
                      return {
                        value: row?.total_count ?? 0,
                        year,
                        finance_source_id: row?.finance_source_id ?? null,
                      };
                    }),
                  })),
                };
              })()}
              onClick={(p) => {
                const d = p.data || {};
                const year = Number(d.year);
                if (!Number.isFinite(year)) return;
                void onSelectYearFunding({
                  year,
                  finance_source_id: (d.finance_source_id as number | null) ?? null,
                });
              }}
            />
          </HospitalSlideBox>
        ),
      },
      {
        id: "hospital-slide-3",
        element: (
          <HospitalSlideBox
            title={`Количество ПС ${filters.place ?? "КС/ДС"} с исходами лечения по годам`}
            loading={loading.slide3_outcomeYear}
            error={errors.slide3_outcomeYear}
          >
            <HospitalChart
              option={(() => {
                const years = [...new Set(charts.slide3_outcomeYear.map((i) => i.year))].sort((a, b) => a - b);
                const outcomes = [...new Set(charts.slide3_outcomeYear.map((i) => String(i.treatment_outcome_id ?? "null")))];
                return {
                  ...baseAxisOption(),
                  xAxis: { ...(baseAxisOption() as any).xAxis, type: "category", data: years },
                  yAxis: { ...(baseAxisOption() as any).yAxis, type: "value" },
                  series: outcomes.map((id) => ({
                    name: charts.slide3_outcomeYear.find((i) => String(i.treatment_outcome_id ?? "null") === id)?.treatment_outcome_name ?? id,
                    type: "line",
                    smooth: true,
                    data: years.map((year) => {
                      const row = charts.slide3_outcomeYear.find((i) => i.year === year && String(i.treatment_outcome_id ?? "null") === id);
                      return { value: row?.total_count ?? 0, treatment_outcome_id: row?.treatment_outcome_id ?? null };
                    }),
                  })),
                };
              })()}
              onClick={(p) => void onSelectTreatmentOutcome((p.data?.treatment_outcome_id as number | null) ?? null)}
            />
          </HospitalSlideBox>
        ),
      },
      {
        id: "hospital-slide-4",
        element: (
          <HospitalSlideBox
            title={`ПС ${filters.place ?? "КС/ДС"} по кол-ву койко-дней по годам`}
            loading={loading.slide4_bedDaysYear}
            error={errors.slide4_bedDaysYear}
          >
            <HospitalChart
              option={{
                ...baseAxisOption(),
                xAxis: { ...(baseAxisOption() as any).xAxis, type: "category", data: charts.slide4_bedDaysYear.map((i) => i.year) },
                yAxis: { ...(baseAxisOption() as any).yAxis, type: "value" },
                series: [{ type: "line", smooth: true, data: charts.slide4_bedDaysYear.map((i) => ({ value: i.avg_bed_days, year: i.year })) }],
              }}
              onClick={(p) => {
                const y = Number(p.data?.year);
                if (Number.isFinite(y)) void onSelectBedDaysYear(y);
              }}
            />
          </HospitalSlideBox>
        ),
      },
      ...(hasSlide41 ? [{ id: "hospital-slide-41", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по кол-ву койко-дней по месяцам`} loading={loading.slide41_bedDaysMonth} error={errors.slide41_bedDaysMonth}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide41_bedDaysMonth.map((i)=>getMonthLabel(i.month))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide41_bedDaysMonth.map((i)=>({value:i.avg_bed_days,month:i.month}))}]}} onClick={(p)=>{const m=Number(p.data?.month); if(Number.isFinite(m)) void onSelectBedDaysMonth(m);}}/></HospitalSlideBox> }] : []),
      ...(hasSlide42 ? [{ id: "hospital-slide-42", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по кол-ву койко-дней по дням`} loading={loading.slide42_bedDaysDay} error={errors.slide42_bedDaysDay}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide42_bedDaysDay.map((i)=>formatDayNumber(i.date))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide42_bedDaysDay.map((i)=>i.avg_bed_days)}]}}/></HospitalSlideBox> }] : []),
      { id: "hospital-slide-5", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} по ${fundingNameById.get(String(filters.finance_source_id ?? "null")) ?? "источнику"} в ${filters.year ?? "—"} году`} loading={loading.slide5_casesMonth} error={errors.slide5_casesMonth}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide5_casesMonth.map((i)=>getMonthLabel(i.month))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide5_casesMonth.map((i)=>({value:i.total_count,month:i.month}))}]}} onClick={(p)=>{const m=Number(p.data?.month); if(Number.isFinite(m)) void onSelectCasesMonth(m);}}/></HospitalSlideBox> },
      ...(hasSlide6 ? [{ id: "hospital-slide-6", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} по дням`} loading={loading.slide6_casesDay} error={errors.slide6_casesDay}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide6_casesDay.map((i)=>formatDayNumber(i.date))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide6_casesDay.map((i)=>i.total_count)}]}}/></HospitalSlideBox> }] : []),
      { id: "hospital-slide-7", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} в разрезе профилей коек`} loading={loading.slide7_bedProfileYear} error={errors.slide7_bedProfileYear}><HospitalChart option={withHorizontalBarDataZoom({...baseAxisOption(), grid:{top:28,left:170,right:24,bottom:30}, xAxis:{...(baseAxisOption() as any).xAxis,type:"value"}, yAxis:horizontalAxisLabels(bedProfileRows.map((i)=>i.bed_profile_name)), series:[{type:"bar",data:bedProfileRows.map((i)=>({value:i.total_count,bed_profile_id:i.bed_profile_id}))}]})} onClick={(p)=>void onSelectBedProfile((p.data?.bed_profile_id as number | null) ?? null)}/></HospitalSlideBox> },
      ...(hasSlide71 ? [{ id: "hospital-slide-71", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по профилю коек ${selectedBedProfileName ?? filters.bed_profile_id ?? "—"} по месяцам`} loading={loading.slide71_bedProfileMonth} error={errors.slide71_bedProfileMonth}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide71_bedProfileMonth.map((i)=>getMonthLabel(i.month))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide71_bedProfileMonth.map((i)=>({value:i.total_count,month:i.month}))}]}} onClick={(p)=>{const m=Number(p.data?.month); if(Number.isFinite(m)) void onSelectBedProfileMonth(m);}}/></HospitalSlideBox> }] : []),
      ...(hasSlide72 ? [{ id: "hospital-slide-72", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по профилю коек ${selectedBedProfileName ?? filters.bed_profile_id ?? "—"} по дням за ${getMonthLabel(filters.month_p ?? 0)} ${filters.year_p ?? activeDrilldownYear ?? "—"} года`} loading={loading.slide72_bedProfileDay} error={errors.slide72_bedProfileDay}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide72_bedProfileDay.map((i)=>formatDayNumber(i.date))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide72_bedProfileDay.map((i)=>i.total_count)}]}}/></HospitalSlideBox> }] : []),
      { id: "hospital-slide-8", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} в разрезе МКБ-10 в ${activeDrilldownYear ?? "—"} году`} loading={loading.slide8_mkb10Year} error={errors.slide8_mkb10Year}><HospitalChart option={withHorizontalBarDataZoom({...baseAxisOption(), grid:{top:28,left:110,right:24,bottom:30}, xAxis:{...(baseAxisOption() as any).xAxis,type:"value"}, yAxis:horizontalAxisLabels(mkb10Rows.map((i)=>i.mkb10_code), { formatter: (value) => value }), series:[{type:"bar",data:mkb10Rows.map((i)=>({value:i.total_count,mkb10_id:i.mkb10_id}))}]})} onClick={(p)=>void onSelectMkb10((p.data?.mkb10_id as number | null) ?? null)}/></HospitalSlideBox> },
      ...(hasSlide81 ? [{ id: "hospital-slide-81", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по МКБ-10 ${selectedMkb10Code ?? filters.mkb10_id ?? "—"} по месяцам`} loading={loading.slide81_mkb10Month} error={errors.slide81_mkb10Month}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide81_mkb10Month.map((i)=>getMonthLabel(i.month))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide81_mkb10Month.map((i)=>({value:i.total_count,month:i.month}))}]}} onClick={(p)=>{const m=Number(p.data?.month); if(Number.isFinite(m)) void onSelectMkb10Month(m);}}/></HospitalSlideBox> }] : []),
      ...(hasSlide82 ? [{ id: "hospital-slide-82", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по МКБ-10 ${selectedMkb10Code ?? filters.mkb10_id ?? "—"} по дням за ${getMonthLabel(filters.month_m ?? 0)} ${filters.year_m ?? activeDrilldownYear ?? "—"} года`} loading={loading.slide82_mkb10Day} error={errors.slide82_mkb10Day}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide82_mkb10Day.map((i)=>formatDayNumber(i.date))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide82_mkb10Day.map((i)=>i.total_count)}]}}/></HospitalSlideBox> }] : []),
      { id: "hospital-slide-9", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} в разрезе отделений в ${activeDrilldownYear ?? "—"} году`} loading={loading.slide9_departments} error={errors.slide9_departments}><HospitalChart option={withHorizontalBarDataZoom({...baseAxisOption(), grid:{top:28,left:170,right:24,bottom:30}, xAxis:{...(baseAxisOption() as any).xAxis,type:"value"}, yAxis:horizontalAxisLabels(departmentRows.map((i)=>i.department_name)), series:[{type:"bar",data:departmentRows.map((i)=>({value:i.total_count,department_id:i.department_id}))}]})} onClick={(p)=>void onSelectDepartment((p.data?.department_id as number | null) ?? null)}/></HospitalSlideBox> },
      ...(hasSlide10 ? [{ id: "hospital-slide-10", element: <HospitalSlideBox tableHref={casesTableHref} title={`Количество ПС ${filters.place ?? "КС/ДС"} по отделению ${selectedDepartmentName ?? filters.department_id ?? "—"} и врачам в ${filters.year_d ?? activeDrilldownYear ?? "—"} году`} loading={loading.slide10_doctors} error={errors.slide10_doctors}><HospitalChart option={withHorizontalBarDataZoom({...baseAxisOption(), grid:{top:28,left:170,right:24,bottom:30}, xAxis:{...(baseAxisOption() as any).xAxis,type:"value"}, yAxis:horizontalAxisLabels(doctorRows.map((i)=>truncateWords(i.doctor_name, 2))), series:[{type:"bar",data:doctorRows.map((i)=>({value:i.total_count,performer_employee_id:i.performer_employee_id}))}]})} onClick={(p)=>void onSelectDoctor((p.data?.performer_employee_id as string | null) ?? null)}/></HospitalSlideBox> }] : []),
      ...(hasSlide11 ? [{ id: "hospital-slide-11", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по врачу ${selectedDoctorName ?? filters.performer_employee_id ?? "—"} по месяцам`} loading={loading.slide11_doctorMonth} error={errors.slide11_doctorMonth}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide11_doctorMonth.map((i)=>getMonthLabel(i.month))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide11_doctorMonth.map((i)=>({value:i.total_count,month:i.month}))}]}} onClick={(p)=>{const m=Number(p.data?.month); if(Number.isFinite(m)) void onSelectDoctorMonth(m);}}/></HospitalSlideBox> }] : []),
      ...(hasSlide12 ? [{ id: "hospital-slide-12", element: <HospitalSlideBox tableHref={casesTableHref} title={`ПС ${filters.place ?? "КС/ДС"} по врачу ${selectedDoctorName ?? filters.performer_employee_id ?? "—"} по дням за ${getMonthLabel(filters.month_d ?? 0)} ${filters.year_d ?? activeDrilldownYear ?? "—"} года`} loading={loading.slide12_doctorDay} error={errors.slide12_doctorDay}><HospitalChart option={{...baseAxisOption(), xAxis:{...(baseAxisOption() as any).xAxis,type:"category",data:charts.slide12_doctorDay.map((i)=>formatDayNumber(i.date))}, yAxis:{...(baseAxisOption() as any).yAxis,type:"value"}, series:[{type:"bar",data:charts.slide12_doctorDay.map((i)=>i.total_count)}]}}/></HospitalSlideBox> }] : []),
    ];

    // Табличные отчёты (письмо Карасайской ЦРБ): по профилям «охраны материнства» не режутся — в этом режиме не показываем.
    if (!isMotherMode) {
      const reportFilters = {
        mis_id: filters.mis_id ?? undefined,
        place: filters.place ?? null,
        year: filters.year ?? undefined,
        month: filters.month ?? undefined,
        day: filters.day ?? undefined,
        date_from: filters.date_from ?? undefined,
        date_to: filters.date_to ?? undefined,
      };
      const casesHref = (p: { department_id?: number | null; finance_source_id?: number | null; place?: string | null }) => {
        const url = new URL(casesTableHref, window.location.origin);
        const setOrDrop = (k: string, v: unknown) => (v == null || v === "" ? url.searchParams.delete(k) : url.searchParams.set(k, String(v)));
        setOrDrop("department_id", p.department_id);
        setOrDrop("finance_source_id", p.finance_source_id);
        setOrDrop("place", p.place);
        if (filters.month != null) url.searchParams.set("month", String(filters.month));
        if (filters.day != null) url.searchParams.set("day", String(filters.day));
        return url.pathname + url.search;
      };
      slides.push(
        { id: "hospital-report-dept-finance", element: <HospitalDeptFinanceSlide filters={reportFilters} fetcher={fetchHospitalReport} Container={SlideContainer} casesHref={casesHref} /> },
        { id: "hospital-report-dept-services", element: <HospitalDeptServicesSlide filters={reportFilters} fetcher={fetchHospitalReport} Container={SlideContainer} /> },
      );
    }

    // Раздел «Контроль качества медицинских карт» — 5 плиток в конец (scope=hospital, живьём).
    slides.push(
      ...buildQualitySlides("hospital", {
        mis_id: filters.mis_id,
        place: filters.place,
        year: filters.year,
        month: filters.month,
        day: filters.day,
        date_from: filters.date_from,
        date_to: filters.date_to,
        bed_profiles: isMotherMode
          ? MOTHER_BED_PROFILE_OPTIONS.map((o) => o.name).join("||")
          : undefined,
      })
    );

    return slides;
  }, [
    filters.mis_id,
    filters.place,
    filters.year,
    filters.month,
    filters.day,
    filters.date_from,
    filters.date_to,
    charts.slide1_place,
    charts.slide2_yearFunding,
    charts.slide3_outcomeYear,
    charts.slide4_bedDaysYear,
    charts.slide41_bedDaysMonth,
    charts.slide42_bedDaysDay,
    charts.slide5_casesMonth,
    charts.slide6_casesDay,
    charts.slide7_bedProfileYear,
    charts.slide71_bedProfileMonth,
    charts.slide72_bedProfileDay,
    charts.slide8_mkb10Year,
    charts.slide81_mkb10Month,
    charts.slide82_mkb10Day,
    charts.slide9_departments,
    charts.slide10_doctors,
    charts.slide11_doctorMonth,
    charts.slide12_doctorDay,
    loading.slide1_place,
    loading.slide2_yearFunding,
    loading.slide3_outcomeYear,
    loading.slide4_bedDaysYear,
    loading.slide41_bedDaysMonth,
    loading.slide42_bedDaysDay,
    loading.slide5_casesMonth,
    loading.slide6_casesDay,
    loading.slide7_bedProfileYear,
    loading.slide71_bedProfileMonth,
    loading.slide72_bedProfileDay,
    loading.slide8_mkb10Year,
    loading.slide81_mkb10Month,
    loading.slide82_mkb10Day,
    loading.slide9_departments,
    loading.slide10_doctors,
    loading.slide11_doctorMonth,
    loading.slide12_doctorDay,
    errors.slide1_place,
    errors.slide2_yearFunding,
    errors.slide3_outcomeYear,
    errors.slide4_bedDaysYear,
    errors.slide41_bedDaysMonth,
    errors.slide42_bedDaysDay,
    errors.slide5_casesMonth,
    errors.slide6_casesDay,
    errors.slide7_bedProfileYear,
    errors.slide71_bedProfileMonth,
    errors.slide72_bedProfileDay,
    errors.slide8_mkb10Year,
    errors.slide81_mkb10Month,
    errors.slide82_mkb10Day,
    errors.slide9_departments,
    errors.slide10_doctors,
    errors.slide11_doctorMonth,
    errors.slide12_doctorDay,
    hasSlide41,
    hasSlide42,
    hasSlide71,
    hasSlide72,
    hasSlide81,
    hasSlide82,
    hasSlide10,
    hasSlide11,
    hasSlide12,
    isMotherMode,
    casesTableHref,
    filters.place,
    filters.finance_source_id,
    filters.year,
    onSelectPlace,
    onSelectYearFunding,
    onSelectTreatmentOutcome,
    onSelectBedDaysYear,
    onSelectBedDaysMonth,
    onSelectCasesMonth,
    onSelectBedProfile,
    onSelectBedProfileMonth,
    onSelectMkb10,
    onSelectMkb10Month,
    onSelectDepartment,
    onSelectDoctor,
    onSelectDoctorMonth,
  ]);

  const pages = useMemo(
    () => chunkSlides(visibleSlides, layoutByPage),
    [visibleSlides, layoutByPage],
  );

  useEffect(() => {
    if (!pages.length) {
      setPageIndex(0);
      return;
    }
    if (wantLastPageRef.current) {
      // возврат из «Диспансерного учёта»: встать на последнюю страницу
      wantLastPageRef.current = false;
      setPageIndex(pages.length - 1);
      return;
    }
    setPageIndex((prev) => Math.min(prev, pages.length - 1));
  }, [pages.length]);

  useEffect(() => {
    writeLayoutByPageToStorage(layoutByPage);
  }, [layoutByPage]);

  const currentLayout = layoutByPage[pageIndex] ?? DEFAULT_LAYOUT;
  const bootstrapError = errors.bootstrap;

  const content =
    pathname.startsWith("/dashboards/hospital") ||
    pathname.startsWith("/dashboards/mother-hospital") ? (
    <LayoutContext.Provider value={currentLayout}>
      <SlidePresentationContext.Provider
        value={{ activeSlideId, setActiveSlideId }}
      >
        <div style={{ minHeight: "100vh", background: "#083264", padding: 10 }}>
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <div
              style={{
                background: "#0e2b4b",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.2)",
                padding: 12,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    fontSize: 14,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  подразделение:
                  <MisIdDropdown
                    value={filters.mis_id ?? ""}
                    options={orgOptions}
                    onChange={(value) => {
                      const v = value.trim();
                      void onSelectMisId(v);
                      setPageIndex(0);
                    }}
                  />
                </label>

                <button
                  onClick={() => void resetFiltersAndReload()}
                  style={{
                    height: 36,
                    padding: "0 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "#083264",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Сбросить все
                </button>

                <button
                  onClick={() => void reloadAllVisibleData()}
                  style={{
                    height: 36,
                    padding: "0 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "#083264",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Обновить
                </button>

                <FiltersDropdown chips={chips} onRemove={clearFilter} />
                <PeriodCalendar
                  dateFrom={filters.date_from ?? undefined}
                  dateTo={filters.date_to ?? undefined}
                  onApply={(from, to) => { patchFilters({ date_from: from, date_to: to }); setPageIndex(0); }}
                  onClear={() => patchFilters({ date_from: null, date_to: null })}
                />

                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                    style={{
                      height: 34,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "#083264",
                      color: "#fff",
                      padding: "0 10px",
                      cursor: "pointer",
                    }}
                  >
                    Назад
                  </button>
                  <button
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                    style={{
                      height: 34,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "#083264",
                      color: "#fff",
                      padding: "0 10px",
                      cursor: "pointer",
                    }}
                  >
                    {"<<"}
                  </button>
                  <button
                    onClick={() => {
                      if (pageIndex >= pages.length - 1) {
                        // последняя закладка стационара -> Диспансерный учёт
                        const mis = (filters.mis_id || "").trim();
                        window.location.href = "/dashboards/duchet" + (mis ? "?mis_id=" + encodeURIComponent(mis) : "");
                        return;
                      }
                      setPageIndex((p) =>
                        Math.min((pages.length || 1) - 1, p + 1),
                      );
                    }}
                    title={pageIndex >= pages.length - 1 ? "Далее: Диспансерный учёт" : undefined}
                    style={{
                      height: 34,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "#083264",
                      color: "#fff",
                      padding: "0 10px",
                      cursor: "pointer",
                    }}
                  >
                    {">>"}
                  </button>
                  <select
                    value={String(currentLayout)}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      if (!isValidLayout(next)) return;
                      setLayoutByPage((prev) => ({
                        ...prev,
                        [pageIndex]: next,
                      }));
                      setActiveSlideId(null);
                    }}
                    style={{
                      height: 34,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "#083264",
                      color: "#fff",
                      padding: "0 8px",
                    }}
                  >
                    {PAGE_LAYOUTS.map((layout) => (
                      <option key={layout} value={layout}>
                        {layout}
                      </option>
                    ))}
                  </select>

                  <details style={{ position: "relative" }}>
                    <summary
                      style={{
                        color: "#fff",
                        cursor: "pointer",
                        listStyle: "none",
                      }}
                    >
                      ☰
                    </summary>
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 6px)",
                        background: "#083264",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 8,
                        padding: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        zIndex: 20000,
                        minWidth: 220,
                      }}
                    >
                      {[
                        { label: "Главная", href: "/" },
                        { label: "Dashboards АПП", href: "/dashboards/app" },
                        { label: "Dashboards КС", href: "/dashboards/hospital?place=%D0%9A%D0%A1" },
                        { label: "Охрана материнства", href: "/dashboards/mother-hospital" },
                        { label: "Dashboards ДС", href: "/dashboards/hospital?place=%D0%94%D0%A1" },
                        { label: "Ситуационный центр", href: "/dashboards/situation-center" },
                        { label: "Диспансерный учёт", href: "/dashboards/duchet" },
                      ].map((route) => (
                        <a
                          key={route.href}
                          href={route.href}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            background: "transparent",
                            border: "none",
                            textAlign: "left",
                            color: "#fff",
                            cursor: "pointer",
                            padding: "6px 8px",
                            borderRadius: 6,
                            textDecoration: "none",
                          }}
                        >
                          {route.label}
                        </a>
                      ))}
                    </div>
                  </details>
                </div>
              </div>

              {bootstrapError ? <ErrorBox error={bootstrapError} /> : null}
            </div>

            <div
              style={{
                height: "calc(100vh - 136px)",
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!pages.length ? (
                <div
                  style={{ color: "#fff", textAlign: "center", paddingTop: 32 }}
                >
                  Нет данных
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {pages.map((pageSlides, idx) => {
                    const layout = layoutByPage[idx] ?? DEFAULT_LAYOUT;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: idx === pageIndex ? "grid" : "none",
                          gap: 8,
                          flex: 1,
                          minHeight: 0,
                          overflow: "hidden",
                          alignItems: "stretch",
                          ...getGridTemplate(layout),
                        }}
                      >
                        {renderPageCells(pageSlides, layout)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </SlidePresentationContext.Provider>
    </LayoutContext.Provider>
  ) : null;

  return <ErrorBoundary>{content}</ErrorBoundary>;
}
